import { useState } from "react";
import { Card, Row, Col, Button, Form, Alert, Table, Tag, Badge, Tabs, Empty } from "antd";
import { 
    UploadOutlined, 
    FileTextOutlined, 
    CheckCircleOutlined,
    WarningOutlined,
    FontSizeOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { uploadTemplate, uploadDataFile } from "../../core/api/pdfParser";
import useGlobalFilters from "../../hooks/useGlobalFilters";

function UploadTemplate() {
    const navigate = useNavigate();
    const { brandIds, hasSelectedBrands } = useGlobalFilters();
    const currentBrandId = brandIds.length > 0 ? brandIds[0] : null;
    
    const [templateFile, setTemplateFile] = useState(null);
    const [dataFile, setDataFile] = useState(null);
    const [templateName, setTemplateName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [dataResult, setDataResult] = useState(null);

    const handleTemplateUpload = async () => {
        if (!templateFile) {
            setError("Please select a PDF template file");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await uploadTemplate(templateFile, templateName || templateFile.name, currentBrandId);
            setUploadResult(result);
        } catch (err) {
            setError(err.response?.data?.error || err.message || "Upload failed");
        } finally {
            setLoading(false);
        }
    };

    const handleDataUpload = async () => {
        if (!dataFile) {
            setError("Please select a data file (Excel/CSV)");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await uploadDataFile(dataFile, null, currentBrandId);
            setDataResult({
                ...result.dataFile,
                columns: result.dataFile.columns,
                rowCount: result.dataFile.row_count,
                preview: result.dataFile.preview_data,
            });
        } catch (err) {
            setError(err.response?.data?.error || err.message || "Upload failed");
        } finally {
            setLoading(false);
        }
    };

    const placeholderColumns = [
        {
            title: "Placeholder",
            dataIndex: "name",
            key: "name",
            render: (name) => <Tag color="blue">{`{${name}}`}</Tag>,
        },
        {
            title: "Font",
            dataIndex: "font",
            key: "font",
            render: (font) => <span style={{ fontFamily: "monospace" }}>{font}</span>,
        },
        {
            title: "Size",
            dataIndex: "size",
            key: "size",
            render: (size) => `${size}pt`,
        },
        {
            title: "Occurrences",
            dataIndex: "occurrences",
            key: "occurrences",
            render: (occ) => <Badge count={occ?.length || 0} style={{ backgroundColor: "#52c41a" }} />,
        },
    ];

    const dataColumns = dataResult?.columns?.map((col) => ({
        title: col,
        dataIndex: col,
        key: col,
        ellipsis: true,
    })) || [];

    // If no brand selected, show message
    if (!hasSelectedBrands) {
        return (
            <Card style={{ textAlign: "center", padding: 60 }}>
                <Empty 
                    description="Please select a brand to upload templates and data"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Card>
        );
    }

    return (
        <div>
            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    style={{ marginBottom: 16 }}
                />
            )}

            <Row gutter={[24, 24]}>
                {/* Template Upload */}
                <Col xs={24} lg={12}>
                    <Card 
                        title={
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    background: "#1a1a2e",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <FileTextOutlined style={{ color: "#fff", fontSize: 16 }} />
                                </div>
                                <span style={{ fontWeight: 600 }}>Step 1: Upload PDF Template</span>
                            </div>
                        }
                        className="h-100"
                        style={{ 
                            borderRadius: 12,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                        }}
                        headStyle={{ borderBottom: "1px solid #f0f0f0" }}
                    >
                        <Form layout="vertical">
                            <Form.Item label={<span style={{ fontWeight: 500 }}>Template Name (optional)</span>}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., Marketing Letter Template"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    style={{ borderRadius: 8 }}
                                />
                            </Form.Item>

                            <Form.Item label={<span style={{ fontWeight: 500 }}>PDF Template File</span>}>
                                <div className="d-flex gap-2">
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".pdf"
                                        onChange={(e) => setTemplateFile(e.target.files[0])}
                                        style={{ borderRadius: 8 }}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<UploadOutlined />}
                                        onClick={handleTemplateUpload}
                                        loading={loading}
                                        disabled={!templateFile}
                                        style={{ 
                                            borderRadius: 8,
                                            background: "#1677ff",
                                            borderColor: "#1677ff",
                                            color: "#fff"
                                        }}
                                    >
                                        Upload
                                    </Button>
                                </div>
                                <small style={{ color: "#8c8c8c", marginTop: 4, display: "block" }}>
                                    Upload a PDF with placeholders like <code style={{ background: "#f5f5f5", padding: "2px 6px", borderRadius: 4 }}>{"{FIRSTNAME}"}</code>, <code style={{ background: "#f5f5f5", padding: "2px 6px", borderRadius: 4 }}>{"{LASTNAME}"}</code>
                                </small>
                            </Form.Item>
                        </Form>

                        {uploadResult && (
                            <div className="mt-3">
                                <Alert
                                    message="Template Uploaded Successfully"
                                    description={`Found ${uploadResult.template?.placeholders?.length || 0} unique placeholders`}
                                    type="success"
                                    showIcon
                                    icon={<CheckCircleOutlined />}
                                />

                                {/* Font Status */}
                                {uploadResult.fonts && (
                                    <div className="mt-3">
                                        <h6>Font Status</h6>
                                        {uploadResult.fonts.missing_fonts?.length > 0 ? (
                                            <Alert
                                                message="Missing Fonts"
                                                description={
                                                    <div>
                                                        <p>The following fonts are needed but not available:</p>
                                                        <ul>
                                                            {uploadResult.fonts.missing_fonts.map((f) => (
                                                                <li key={f}>{f}</li>
                                                            ))}
                                                        </ul>
                                                        <p>Upload these fonts in the Fonts tab, or text will use Helvetica fallback.</p>
                                                    </div>
                                                }
                                                type="warning"
                                                showIcon
                                                icon={<WarningOutlined />}
                                            />
                                        ) : (
                                            <Alert
                                                message="All fonts available"
                                                type="success"
                                                showIcon
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </Col>

                {/* Data Upload */}
                <Col xs={24} lg={12}>
                    <Card 
                        title={
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    background: "#16213e",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <FileTextOutlined style={{ color: "#fff", fontSize: 16 }} />
                                </div>
                                <span style={{ fontWeight: 600 }}>Step 2: Upload Data File</span>
                            </div>
                        }
                        className="h-100"
                        style={{ 
                            borderRadius: 12,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                        }}
                        headStyle={{ borderBottom: "1px solid #f0f0f0" }}
                    >
                        <Form layout="vertical">
                            <Form.Item label={<span style={{ fontWeight: 500 }}>Excel/CSV Data File</span>}>
                                <div className="d-flex gap-2">
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={(e) => setDataFile(e.target.files[0])}
                                        style={{ borderRadius: 8 }}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<UploadOutlined />}
                                        onClick={handleDataUpload}
                                        loading={loading}
                                        disabled={!dataFile}
                                        style={{ 
                                            borderRadius: 8,
                                            background: "#1677ff",
                                            borderColor: "#1677ff",
                                            color: "#fff"
                                        }}
                                    >
                                        Upload
                                    </Button>
                                </div>
                                <small style={{ color: "#8c8c8c", marginTop: 4, display: "block" }}>
                                    Upload Excel (.xlsx) or CSV file containing your data rows
                                </small>
                            </Form.Item>
                        </Form>

                        {dataResult && (
                            <div className="mt-3">
                                <Alert
                                    message="Data File Uploaded"
                                    description={`Found ${dataResult.columns?.length || 0} columns and ${dataResult.rowCount || 0} rows`}
                                    type="success"
                                    showIcon
                                />

                                {/* Column List */}
                                {dataResult.columns?.length > 0 && (
                                    <div className="mt-3">
                                        <h6>Available Columns</h6>
                                        <div className="d-flex flex-wrap gap-1">
                                            {dataResult.columns.map((col) => (
                                                <Tag key={col} color="geekblue">{col}</Tag>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Preview */}
                                {dataResult.preview?.length > 0 && (
                                    <div className="mt-3">
                                        <h6>Data Preview (first 5 rows)</h6>
                                        <div style={{ overflowX: "auto" }}>
                                            <Table
                                                dataSource={dataResult.preview.map((row, idx) => ({ ...row, _key: idx }))}
                                                columns={dataColumns}
                                                rowKey="_key"
                                                size="small"
                                                pagination={false}
                                                scroll={{ x: true }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Full Width Placeholder Preview */}
            {uploadResult?.template?.placeholders?.length > 0 && (
                <Card
                    title={
                        <div className="d-flex align-items-center gap-2">
                            <div style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: "#2c3e50",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}>
                                <EyeOutlined style={{ color: "#fff", fontSize: 16 }} />
                            </div>
                            <span style={{ fontWeight: 600 }}>Placeholder Preview</span>
                            <Tag color="blue">{uploadResult.template.placeholders.length} unique placeholders</Tag>
                        </div>
                    }
                    style={{ 
                        marginTop: 24,
                        borderRadius: 12,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                    }}
                    headStyle={{ borderBottom: "1px solid #f0f0f0" }}
                >
                    <Tabs
                        defaultActiveKey="table"
                        size="small"
                        items={[
                            {
                                key: "table",
                                label: (
                                    <span>
                                        <FontSizeOutlined style={{ marginRight: 6 }} />
                                        Table View
                                    </span>
                                ),
                                children: (
                                    <Table
                                        dataSource={uploadResult.template.placeholders}
                                        columns={placeholderColumns}
                                        rowKey="name"
                                        size="small"
                                        pagination={false}
                                        style={{ marginTop: 12 }}
                                    />
                                ),
                            },
                        ]}
                    />
                </Card>
            )}

            {/* Next Steps */}
            {uploadResult && dataResult && (
                <Card 
                    style={{ 
                        marginTop: 24,
                        borderRadius: 12,
                        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                        border: "none"
                    }}
                >
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 style={{ color: "#fff", marginBottom: 4, fontWeight: 600 }}>
                                Ready to Generate!
                            </h5>
                            <p style={{ color: "rgba(255,255,255,0.7)", marginBottom: 0 }}>
                                Both template and data are uploaded. Proceed to mapping and generation.
                            </p>
                        </div>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => navigate(`/pdf-parser/generate?templateId=${uploadResult.template?.id}&dataPath=${dataResult.id}`)}
                            style={{
                                background: "#c62828",
                                borderColor: "#c62828",
                                borderRadius: 8,
                                fontWeight: 600,
                                height: 44,
                                paddingLeft: 24,
                                paddingRight: 24
                            }}
                        >
                            Continue to Generate →
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
}

export default UploadTemplate;
